<template>
	<AppAutoSuggest
		:options="options"
		:active-item="activeItem"
		:auto-navigation="autoNavigation"
		:is-loading="isLoading"
		:placeholder="$t('autosuggests.purchaseorders.placeholder')"
		@input="$emit('input', $event)"
		@load="loadOptions($event)"
	/>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from 'vue';
import type { SelectItem } from '@developers/scoro-ui';
import { AppAutoSuggest, AutoSuggestDefaultEmits } from '@/features/App';
import {
	usePurchaseOrderAutoSuggestLazyQuery,
	type PurchaseOrder,
	PurchaseOrderAutoSuggestDocument,
} from '@/generated/graphql';
import { DEFAULT_AUTO_SUGGEST_LIMIT } from '@/constants/constants';

export default defineComponent({
	components: { AppAutoSuggest },

	props: {
		/**
		 * Selected dropdown item to display without fetching all list
		 */
		item: {
			type: Object as PropType<
				| (Partial<PurchaseOrder> &
						Pick<PurchaseOrder, 'id' | 'number' | 'mainContact'>)
				| undefined
			>,
			required: false,
			default: undefined,
		},

		autoNavigation: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: AutoSuggestDefaultEmits,

	setup(props) {
		const {
			loading: isLoading,
			load,
			onResult,
		} = usePurchaseOrderAutoSuggestLazyQuery({
			purchaseOrdersListInput: {
				filters: {
					searchKeywords: '',
				},
				pager: {
					limit: DEFAULT_AUTO_SUGGEST_LIMIT,
					startFrom: 0,
				},
			},
		});

		const options = ref<SelectItem[]>([]);

		onResult((result) => {
			if (result.data?.PurchaseOrdersList) {
				options.value =
					result.data.PurchaseOrdersList.map((row) => {
						return {
							id: row.id,
							title: row.number,
							subtitle: row.mainContact?.name,
						};
					}) || [];
			}
		});

		const activeItem = computed<SelectItem | undefined>(() => {
			return props.item
				? {
						id: props.item.id,
						title: props.item.number,
						subtitle: props.item.mainContact?.name,
					}
				: undefined;
		});

		const loadOptions = async (searchKeywords: string) => {
			load(PurchaseOrderAutoSuggestDocument, {
				purchaseOrdersListInput: {
					filters: {
						searchKeywords,
					},
					pager: {
						limit: DEFAULT_AUTO_SUGGEST_LIMIT,
						startFrom: 0,
					},
				},
			});
		};

		return {
			activeItem,
			options,
			isLoading,
			loadOptions,
		};
	},
});
</script>
